import api from "app/api";
import { globalMessageHandler } from "common";
import useSettings from "features/settings/useSettings";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";

export type CoordinatorReport2Data = {
  amount: number;
  boat: string;
  admin_fee_amount: number;
  canceled_amount: number;
  canceled_count: number;
  closed_event_count: number;
  canceled_guests: number;
  count: number;
  email: string;
  guests: number;
  name: string;
  partner: string;
  partner_group: string;
  program: string;
  program_variant: string;
  t: string;
  color?: string;
  partner_color?: string;
  boat_color?: string;
  partner_group_color?: string;
  // street_ fields
  street_amount: number;
  street_admin_fee_amount: number;
  street_refund_amount: number;
  street_refund_guests: number;
  street_guests: number;
  street_count: number;
  street_canceled_amount: number;
  street_canceled_guests: number;
  street_canceled_count: number;
};

export type CoordinatorReport2FilterType = {
  from: string;
  to: string;
  boatIds?: number[];
  programIds?: number[];
  currency?: null | "EUR" | "HUF";
};

export type CoordinatorReport2DataContextType = {
  data: CoordinatorReport2Data[];
  filter: CoordinatorReport2FilterType;
  setFilter: (filter: CoordinatorReport2FilterType) => void;
  load: () => Promise<void>;
  loading: boolean;
  firstLoaded: boolean;
};

export const CoordinatorReport2DataContext =
  createContext<CoordinatorReport2DataContextType | null>(null);

export default function useCoordinatorReport2Data() {
  const [filter, setFilter] = useState<CoordinatorReport2FilterType>({
    from: moment().startOf("day").format("YYYY-MM-DD"),
    to: moment().endOf("day").format("YYYY-MM-DD"),
  });
  const [loading, setLoading] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [data, setData] = useState<CoordinatorReport2Data[]>([]);
  const [settings] = useSettings({ key: "simpleReport" });

  async function load() {
    setLoading(true);
    const { success, data, error } = await api.get(
      "reports/advanced-coordinator",
      {
        params: {
          ...filter,
          currency: settings.currency,
          dateType: settings.dateType,
          boatIds: [],
          programIds: [],
        },
      },
    );
    setLoading(false);
    if (success) {
      if (!firstLoaded) {
        setFirstLoaded(true);
      }
      setData(data);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  useEffect(() => {
    load();
  }, [
    filter.programIds,
    filter.boatIds,
    filter.from,
    filter.to,
    settings.currency,
    settings.dateType,
  ]);

  return { data, filter, setFilter, load, loading, firstLoaded };
}

export function useCoordinatorReport2DataContext() {
  const context = useContext(CoordinatorReport2DataContext);
  if (!context) {
    throw Error(
      "useCoordinatorReport2DataContext cannot be used outside of CoordinatorReport2DataProvider.",
    );
  }
  return context;
}
